import React from 'react';

import { reverse } from 'named-urls';

import { useMatch } from 'react-router-dom';

import { GridEventListener, GridRowParams, GridValidRowModel } from '@mui/x-data-grid';

import Record from 'ui/Record/Record';

import { formatMoment } from 'utils/formatters/timezone';

import { MainHeader } from 'components/MainHeader/MainHeader';

import { Button } from 'components/Button';

import routes from 'core/routes';

import { useNavBarHeight } from 'utils/hooks/navbar';

import { getEntityTranslation } from 'stores/EntityDetails/utils';
import { DATETIME_COLUMN_MIN_WIDTH, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, EntityModules } from 'core/constants';
import Grid, { defaultFormatters, defaultOperators } from 'components/Grid';
import { ColumnDefinition } from 'components/Grid/types';
import { ActiveStatusOperator } from 'components/Grid/operators/ActiveStatus';
import { useStore } from 'utils/hooks/store';

import ActionCell from './ActionCell';
import StatusCell from './StatusCell';

import AudienceFilteringOperator from './AudienceFilteringOperator';
import { StatusCellProps } from './StatusCell/StatusCell';

export const basicColumns = [
  {
    field: 'name',
    headerName: 'Form name',
    nativeFilter: 'name',
  },
  {
    field: 'audience',
    headerName: 'Audience',
    nativeFilter: 'audience',
    valueGetter: (_value: unknown, row: GridValidRowModel): string[] => row.settings.audience,
    valueFormatter: (value: string[] | undefined): string => {
      if (!value || !Array.isArray(value) || value.length === 0) {
        return '';
      }
      if (value.length === 1) {
        return value[0].charAt(0).toUpperCase() + value[0].slice(1);
      }
      return 'Public & User';
    },
    filterOperators: AudienceFilteringOperator,
  },
  {
    field: 'updatedAt',
    nativeFilter: 'updatedAt',
    headerName: 'Last published',
    minWidth: DATETIME_COLUMN_MIN_WIDTH,
    filterOperators: defaultOperators.date_time(),
    valueFormatter: defaultFormatters.date_time,
    renderCell: ({ value }: { value: string }) => formatMoment(value, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR),
  },
  {
    field: 'status',
    nativeFilter: 'active',
    renderCell: ({ row, colDef }: { row: StatusCellProps['row']; colDef: ColumnDefinition }) => (
      <StatusCell row={row} actions={colDef.actions} />
    ),
    headerName: 'Status',
    filterOperators: ActiveStatusOperator(),
  },
  {
    maxWidth: 50,
    headerName: '',
    type: 'actions',
    field: 'actions',
    ariaLabel: 'actions',
    renderCell: ({ row }: { row: { uuid: string; name?: string } }) => <ActionCell rowData={row} />,
    sortable: false,
  },
] as unknown as ColumnDefinition[];

const EventForms = (): React.ReactElement => {
  const { routing } = useStore();
  const navBarHeight = useNavBarHeight();
  const match = useMatch(routes.dashboard.settings.objects.details.formBuilder.toString());

  const { moduleName } = match?.params as { moduleName: string };

  const onRowClick: GridEventListener<'rowClick'> = (form: GridRowParams): void => {
    const path = reverse(routes.dashboard.settings.objects.details.formBuilder.toString(), {
      moduleName: EntityModules.Events,
    });
    routing.push(`${path + form.row.uuid}/`);
  };

  return (
    <Record
      navBarHeight={navBarHeight}
      Header={
        <MainHeader
          title="Forms"
          breadcrumbs={[
            {
              label: 'Objects',
              link: '',
            },
            {
              label: getEntityTranslation[moduleName as EntityModules].plural,
              link: reverse(routes.dashboard.settings.objects.details.formBuilder.toString(), { moduleName }),
            },
            {
              label: 'Forms',
              link: reverse(routes.dashboard.settings.objects.details.formBuilder.toString(), { moduleName }),
            },
          ]}
          action={
            <Button
              variant="contained"
              onClick={() => {
                const path = reverse(routes.dashboard.settings.objects.details.formBuilder.toString(), {
                  moduleName: EntityModules.Events,
                });
                routing.push(`${path}new/`);
              }}
            >
              New
            </Button>
          }
        />
      }
      Content={(onError) => (
        <Grid
          disableExport
          onError={onError}
          disableColumnSelector
          onRowClick={onRowClick}
          basicColumns={basicColumns}
          entity={`${moduleName} forms`}
          key={`form_builder_${moduleName}`}
          dataURL={`/api/entities/${moduleName}/forms`}
          customNoRowsText="No forms have been created yet"
        />
      )}
    />
  );
};

export default EventForms;
